import { FieldsToLookupByMasterAttributeId } from "global/Application";
import * as FAM from "model/FlexibleAttributeModel";
import { ArrayToLookupByPredicate, Memoize } from "../Utility/TSFunctions";
import { BaseJsonTransformer } from "./IJsonTransformer";

/**
 * Transformers:
 * Option Set Item Cleanup
 * https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/493990
 */
export class Sprint33Transformer extends BaseJsonTransformer {
    config: FAM.IGroupConfigurationConfig;
    json: FAM.IGroupConfigurationData;

    benefitsLookup: Record<string, FAM.IBenefit>;
    plansLookup: Record<string, FAM.IPlan>;
    benefitPlans: Record<string, FAM.IPlan[]>;

    constructor({ config, json }: { config: FAM.IGroupConfigurationConfig, json: FAM.IGroupConfigurationData }) {
        super();
        this.config = config;

        this.json = json;

        const benefitPlans: Record<string, FAM.IPlan[]> = {};

        const benefitsLookup = ArrayToLookupByPredicate(this.json.benefits, e => e.id);
        const plansLookup = ArrayToLookupByPredicate(this.json.plans, e => e.id);

        this.json.plans.forEach(plan => {
            plan.benefitRefs.forEach(eachBenefitRef => {
                // make sure it exists
                const plans = benefitPlans[eachBenefitRef.id] ?? [];
                if (!plans.includes(plan))
                    plans.push(plan);

                benefitPlans[eachBenefitRef.id] = plans;

            })
            // beenfit id -> plan1, plan2,...
            // plan1id -> all benefits and find teelehealth
        });

        this.benefitsLookup = benefitsLookup;
        this.plansLookup = plansLookup;
        this.benefitPlans = benefitPlans;

    }

    ProductMapping(): Record<string, FAM.IEntityReferenceValue> {
        // Mapping:
        // "EPO  (Limited OON )"  (02b74e02-6e31-ee11-bdf3-000d3a305861) -> "EPO (Limited OON)" (05a4b8e6-6f31-ee11-bdf3-000d3a985e8d)
        // "Integrated Rx without mail order" (9b7584de-1118-ed11-b83c-00224804e6b3) -> "Integrated Rx w/o Mail Order" (99f53a51-f24b-ec11-8f8e-000d3a319494)
        return {
            "02b74e02-6e31-ee11-bdf3-000d3a305861": {
                type: 'entity',
                entityType: 'bcbs_config_optionsetitem',
                id: "05a4b8e6-6f31-ee11-bdf3-000d3a985e8d",
                name: "EPO (Limited OON)",
            },
            "9b7584de-1118-ed11-b83c-00224804e6b3": {
                type: 'entity',
                entityType: 'bcbs_config_optionsetitem',
                id: "99f53a51-f24b-ec11-8f8e-000d3a319494",
                name: "Integrated Rx w/o Mail Order",
            },
        }
    }

    TelehealthMapping(): Record<string, FAM.IEntityReferenceValue> {
        // Mapping:
        // NA - TH - Teladoc - 36d4cdd2-af51-ee11-be6f-000d3a305861
        // NA - TH - MDLIVE - c7ab9fba-af51-ee11-be6f-000d3a305861
        // NA - TH - CloseKnit - 077f0016-4374-ee11-9ae7-000d3a305861
        // NA - OPH - Teladoc - 2cf56bab-0488-ee11-8179-00224808116e
        // NA - OPH - MDLIVE - 7132af82-0488-ee11-8179-00224808116e
        // NA - OPH - CloseKnit - 2250b961-0488-ee11-8179-00224808116e
        return {
            "36d4cdd2-af51-ee11-be6f-000d3a305861": {
                type: 'entity',
                entityType: 'bcbs_config_optionsetitem',
                id: "2cf56bab-0488-ee11-8179-00224808116e",
                name: "Teladoc",
            },
            "c7ab9fba-af51-ee11-be6f-000d3a305861": {
                type: 'entity',
                entityType: 'bcbs_config_optionsetitem',
                id: "7132af82-0488-ee11-8179-00224808116e",
                name: "MDLIVE",
            },
            "077f0016-4374-ee11-9ae7-000d3a305861": {
                type: 'entity',
                entityType: 'bcbs_config_optionsetitem',
                id: "2250b961-0488-ee11-8179-00224808116e",
                name: "CloseKnit",
            },
        }
    }

    async before(json: FAM.IGroupConfigurationData): Promise<void> {

    }

    async transformBenefit(benefit: FAM.IBenefit): Promise<boolean | void> {

        const categories = this.config.cache.map_bcbs_config_category_named;

        // cache list of fields upon first call
        const fieldInfosMemoized = Memoize(() => {
            return FieldsToLookupByMasterAttributeId(this.config, benefit.fields);
        });

        // Option Set Item Cleanup
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/493990
        // * applies to medical and pharmacy benefits
        // * one from medical and the other from pharmacy are sharing same option set items
        // * during solution management a duplicate optionsetitem was created and these two should be replaced with the original one
        if (benefit.bcbs_config_categoryid === categories['Medical'].bcbs_config_categoryid ||
            benefit.bcbs_config_categoryid === categories['Pharmacy'].bcbs_config_categoryid
        ) {
            // Med Products
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=3f91d4e1-c24b-ec11-8f8e-000d3a3340f2

            // Products
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=dcb83002-7160-ec11-8f8f-000d3a5a07fc

            const optionsetFields = ['3f91d4e1-c24b-ec11-8f8e-000d3a3340f2', 'dcb83002-7160-ec11-8f8f-000d3a5a07fc'];

            optionsetFields.forEach(masterAttributeId => {
                const fieldInfo = fieldInfosMemoized()[masterAttributeId];
                if (fieldInfo) {
                    fieldInfo.forEach(eachFieldInfo => {
                        eachFieldInfo.field.values?.forEach(fieldValue => {
                            if (fieldValue?.value?.type === "entity") {
                                const value = fieldValue.value;
                                if (value && value.id) {
                                    const newValue = this.ProductMapping()[value.id];
                                    if (newValue) {
                                        value.id = newValue.id;
                                        value.name = newValue.name;
                                    }
                                }
                            }
                        });
                    });
                }
            });
        }

        // --------------------------------------------------------------------------------------------
        if (benefit.bcbs_config_categoryid === this.config.cache.map_bcbs_config_category_named['Medical'].bcbs_config_categoryid) {
            //Steps
            //Check if either Telehealth Field is blank/null (check appropriate one based on Funding Type)

            //Check if there is a Telehealth Benefit Associated to the Plan the Medical Beneit is Associated to

            //If there is a Telehealth Benefit, set the appropriate Telehealth field on the OPH to the value of the Telehealth Benefit Telehealth Field

            //const outOfNetworkTier = benefit.tiers?.find(t => t.bcbs_name === 'Out of Network');

            //if (!outOfNetworkTier)
            //return;

            // map to array of master attributes, first element in array being the visible one.
            const fieldsByMasterAttrId = fieldInfosMemoized();

            // OPH Telehealth:
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=818b0bc2-a215-ed11-b83c-00224820a835&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=e8366214-9a62-ec11-8f8f-002248040f87
            const OPH_bcbs_telehealth_masterAttribiute = fieldsByMasterAttrId['e8366214-9a62-ec11-8f8f-002248040f87']
            const OPH_bcbs_telehealth = OPH_bcbs_telehealth_masterAttribiute[0];
            let value_OPH_bcbs_telehealth = OPH_bcbs_telehealth.field.values?.[0];//

            if (value_OPH_bcbs_telehealth) {//value_OPH_bcbs_telehealth.value
                if (value_OPH_bcbs_telehealth.value === null) {
                    const associatedPlans = this.benefitPlans[benefit.id] ?? [];

                    associatedPlans.forEach(eachPlan => {
                        eachPlan.benefitRefs.forEach(eachPlanBenefitRef => {
                            const otherBenefit = this.benefitsLookup[eachPlanBenefitRef.id];
                            if (otherBenefit.bcbs_config_categoryid === this.config.cache.map_bcbs_config_category_named['Telehealth'].bcbs_config_categoryid) {
                                const THfieldsByMasterAttrId = FieldsToLookupByMasterAttributeId(this.config, otherBenefit.fields);
                                //Telehealth Telehealth Field
                                // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=818b0bc2-a215-ed11-b83c-00224820a835&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=e25c1e1f-a042-ee11-bdf5-000d3a3bec48
                                const TH_bcbs_telehealth_masterAttribiute = THfieldsByMasterAttrId['e25c1e1f-a042-ee11-bdf5-000d3a3bec48']
                                const TH_bcbs_telehealth = TH_bcbs_telehealth_masterAttribiute[0];
                                if (TH_bcbs_telehealth) {
                                    if (TH_bcbs_telehealth.field.values[0].value !== null) {
                                        //TH_bcbs_telehealth.field.values[0].value.name, .id, type, entitytype
                                        //value_OPH_bcbs_telehealth.value =
                                        const THEntRef = TH_bcbs_telehealth.field.values[0].value as FAM.IEntityReferenceValue;
                                        const TH_TelehealthGUID = THEntRef.id;
                                        const newValue = this.TelehealthMapping()[TH_TelehealthGUID];
                                        if (newValue && value_OPH_bcbs_telehealth) {
                                            value_OPH_bcbs_telehealth.value = newValue;
                                            //value_OPH_bcbs_telehealth?.value?.id = newValue.id;
                                            //value_OPH_bcbs_telehealth?.value?.name = newValue.name;
                                            //value_OPH_bcbs_telehealth?.value?.type = newValue.type;
                                            //value_OPH_bcbs_telehealth?.value?.entityType = newValue.entityType;
                                        }
                                    }
                                }
                            }
                        })
                    })
                }
            }
        }

    }

    async after(json: FAM.IGroupConfigurationData): Promise<void> {

        console.debug("[tran] Sprint33Transformer.after")

        if (!json.transformers)
            json.transformers = {};

        json.transformers.Sprint33 = true

    }
}
